import { useState } from "react";
import { Link } from "react-router-dom";
import {
  MessageSquare, Zap, Shield, BarChart3, Users, Globe, Code, Check,
  ArrowRight, ChevronDown, ChevronUp, Menu, X, Star, TrendingUp,
  Clock, Smartphone, Building, Heart, GraduationCap, ShoppingBag,
  Banknote, Building2
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { SEO } from "@/components/SEO";
import { WhatsAppButton } from "@/components/WhatsAppButton";

const FEATURES = [
  {
    icon: Zap,
    title: "Instant Delivery",
    desc: "Messages delivered in seconds across all Nigerian networks — MTN, Airtel, Glo, 9mobile.",
    color: "bg-amber-50 text-amber-600",
  },
  {
    icon: Shield,
    title: "Enterprise Security",
    desc: "Bank-grade security with end-to-end encryption, audit logs, and SOC-2 compliant infrastructure.",
    color: "bg-[#eef1f8] text-[#0c1e41]",
  },
  {
    icon: BarChart3,
    title: "Real-Time Analytics",
    desc: "Live delivery reports, open rates, and engagement metrics — all in your dashboard.",
    color: "bg-emerald-50 text-emerald-600",
  },
  {
    icon: Users,
    title: "Contact Management",
    desc: "Organize thousands of contacts into smart groups. Import via CSV or our REST API.",
    color: "bg-purple-50 text-purple-600",
  },
  {
    icon: Code,
    title: "Developer API",
    desc: "RESTful API with SDKs for JavaScript, Python, PHP and more. Go live in minutes.",
    color: "bg-indigo-50 text-indigo-600",
  },
  {
    icon: Globe,
    title: "Custom Sender IDs",
    desc: "Send under your brand name instead of random numbers. Fully NCC compliant.",
    color: "bg-rose-50 text-rose-600",
  },
];

const STEPS = [
  {
    step: "01",
    title: "Create Your Account",
    desc: "Sign up in 60 seconds. No paperwork, no credit card required to explore the platform.",
  },
  {
    step: "02",
    title: "Add Credits & Sender ID",
    desc: "Top up your wallet via bank transfer or card. Request your branded sender ID — approved in 24–48 hours.",
  },
  {
    step: "03",
    title: "Send & Track",
    desc: "Compose your message, pick recipients or upload your list, hit send. Watch delivery reports roll in live.",
  },
];

const TESTIMONIALS = [
  {
    name: "Amaka Obi",
    role: "Marketing Director",
    company: "QuickCommerce Nigeria",
    quote:
      "Sendora transformed how we communicate with our 2 million customers. We went from 72% delivery rates to over 99% overnight.",
    rating: 5,
    initials: "AO",
    color: "bg-[#0c1e41]",
  },
  {
    name: "Emeka Eze",
    role: "Chief Technology Officer",
    company: "FinancePro Ltd",
    quote:
      "The API documentation is excellent. Our development team integrated in under an hour. The reliability is second to none.",
    rating: 5,
    initials: "EE",
    color: "bg-emerald-600",
  },
  {
    name: "Fatima Bello",
    role: "Founder",
    company: "StyleHouse Boutique",
    quote:
      "We increased repeat purchases by 35% using targeted SMS campaigns. The ROI is incredible — best ₦2,000 I ever spent.",
    rating: 5,
    initials: "FB",
    color: "bg-purple-600",
  },
];

const PRICING = [
  {
    name: "Starter",
    price: "₦500",
    credits: 100,
    perSMS: "₦5",
    features: ["100 SMS credits", "All Nigerian networks", "Basic analytics", "Email support", "API access"],
    featured: false,
  },
  {
    name: "Basic",
    price: "₦2,000",
    credits: 525,
    perSMS: "₦3.81",
    bonus: "+25 bonus credits",
    features: ["500 + 25 bonus credits", "All networks", "Full analytics", "Priority support", "API access", "Contact groups"],
    featured: true,
  },
  {
    name: "Standard",
    price: "₦3,500",
    credits: 1075,
    perSMS: "₦3.26",
    bonus: "+75 bonus credits",
    features: ["1,000 + 75 bonus credits", "All networks", "Advanced analytics", "Dedicated support", "API access", "Campaigns", "Sender ID (free)"],
    featured: false,
  },
];

const USE_CASES = [
  { icon: Banknote, label: "Banking & Finance", desc: "OTP, transaction alerts, account notifications" },
  { icon: ShoppingBag, label: "E-Commerce", desc: "Order updates, promotions, abandoned cart" },
  { icon: Heart, label: "Healthcare", desc: "Appointment reminders, health alerts" },
  { icon: GraduationCap, label: "Education", desc: "Exam results, school updates, admissions" },
  { icon: Building2, label: "Government", desc: "Public announcements, citizen alerts" },
  { icon: Smartphone, label: "Startups & Tech", desc: "Verification codes, product updates" },
];

const FAQS = [
  {
    q: "What is Sendora?",
    a: "Sendora is Nigeria's premier bulk SMS platform. It lets businesses send SMS messages to thousands of Nigerians instantly, with real-time delivery reporting, contact management, and a developer-friendly API.",
  },
  {
    q: "How does the credit system work?",
    a: "Credits are like a pre-paid balance. Each SMS sent costs 1 credit (a single segment of up to 160 GSM-7 characters). You purchase credit bundles upfront — no monthly commitments or hidden fees.",
  },
  {
    q: "What is a Sender ID and why do I need one?",
    a: "A Sender ID is the name that appears instead of a phone number when your recipients get your message (e.g. 'MYBRAND'). It builds trust and brand recognition. Our team reviews and approves requests within 24–48 hours.",
  },
  {
    q: "Which Nigerian networks are supported?",
    a: "All major networks: MTN, Airtel, Glo, and 9mobile. We route through BulkSMSLive's infrastructure which has direct carrier connections for maximum delivery rates.",
  },
  {
    q: "How quickly are messages delivered?",
    a: "Most messages are delivered within 5–30 seconds of sending. During high-traffic periods or for very large campaigns, it may take a few minutes. You can track delivery status in real time from your dashboard.",
  },
  {
    q: "What payment methods are accepted?",
    a: "We accept bank transfer. Paystack and Flutterwave (card/bank/USSD) are coming very soon. Credits are added to your wallet immediately after payment confirmation.",
  },
  {
    q: "Is there a REST API?",
    a: "Yes! Our full REST API lets you send SMS, manage contacts, check balances, and more — from any language or framework. Full documentation and code examples (cURL, JavaScript, Python, PHP) are available in your dashboard.",
  },
  {
    q: "What is the minimum purchase?",
    a: "Our Starter pack is ₦500 for 100 SMS credits — perfect for testing. There is no subscription or commitment required.",
  },
];

function FAQItem({ q, a }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="border-b border-slate-200 last:border-0">
      <button
        onClick={() => setOpen(!open)}
        className="w-full flex items-center justify-between py-5 text-left"
      >
        <span className="text-base font-semibold text-slate-900 pr-6">{q}</span>
        {open ? (
          <ChevronUp size={18} className="text-[#0c1e41] shrink-0" />
        ) : (
          <ChevronDown size={18} className="text-slate-400 shrink-0" />
        )}
      </button>
      {open && (
        <p className="pb-5 text-slate-600 text-sm leading-relaxed">{a}</p>
      )}
    </div>
  );
}

export default function Landing() {
  const [mobileOpen, setMobileOpen] = useState(false);

  const LANDING_SCHEMAS = [
    {
      "@context": "https://schema.org",
      "@type": "WebSite",
      "name": "Sendora",
      "url": "https://www.sendoras.com",
      "description": "Nigeria's #1 Bulk SMS Platform — send SMS to MTN, Airtel, Glo and 9mobile instantly.",
      "potentialAction": {
        "@type": "SearchAction",
        "target": { "@type": "EntryPoint", "urlTemplate": "https://www.sendoras.com/?q={search_term_string}" },
        "query-input": "required name=search_term_string"
      }
    },
    {
      "@context": "https://schema.org",
      "@type": "SoftwareApplication",
      "name": "Sendora Bulk SMS Platform",
      "applicationCategory": "BusinessApplication",
      "operatingSystem": "Web",
      "url": "https://www.sendoras.com",
      "description": "Send bulk SMS to every Nigerian network instantly. Custom Sender IDs, real-time delivery reports, contact management and REST API.",
      "screenshot": "https://www.sendoras.com/og-sendora.png",
      "featureList": ["Bulk SMS", "Custom Sender IDs", "REST API", "Real-time Analytics", "Contact Management", "Campaign Scheduling", "Birthday SMS", "Team Management"],
      "offers": {
        "@type": "Offer",
        "price": "0",
        "priceCurrency": "NGN",
        "description": "Free to start. Credit packs from ₦500. No monthly fees."
      },
      "aggregateRating": {
        "@type": "AggregateRating",
        "ratingValue": "4.9",
        "reviewCount": "5000",
        "bestRating": "5"
      }
    },
    {
      "@context": "https://schema.org",
      "@type": "FAQPage",
      "mainEntity": FAQS.map(f => ({
        "@type": "Question",
        "name": f.q,
        "acceptedAnswer": { "@type": "Answer", "text": f.a }
      }))
    }
  ];

  return (
    <div className="min-h-screen bg-white font-inter">
      <SEO
        title="Nigeria's #1 Bulk SMS Platform — Send SMS to MTN, Airtel, Glo, 9mobile"
        description="Sendora — send bulk SMS across all Nigerian networks instantly. 99%+ delivery rates, custom Sender IDs, real-time analytics and REST API. Start free, no credit card needed."
        canonical="/"
        keywords="bulk SMS Nigeria, bulk SMS platform, SMS marketing Nigeria, send bulk SMS Nigeria, SMS API Nigeria, OTP SMS Nigeria, SMS gateway Nigeria, MTN bulk SMS, Airtel SMS gateway, Nigerian SMS service"
        schema={LANDING_SCHEMAS}
      />
      {/* ── NAV ── */}
      <nav className="sticky top-0 z-50 bg-white/90 backdrop-blur-sm border-b border-slate-200 shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-2.5">
              <img src="/sendora-logo.webp" alt="Sendora" className="h-8 w-auto" />
            </div>
            <div className="hidden md:flex items-center gap-8">
              {["Features", "How It Works", "Pricing", "API", "FAQ"].map((item) => (
                <a
                  key={item}
                  href={`#${item.toLowerCase().replace(" ", "-")}`}
                  className="text-sm font-medium text-slate-600 hover:text-[#0c1e41] transition-colors"
                >
                  {item}
                </a>
              ))}
              <Link to="/blog" className="text-sm font-medium text-slate-600 hover:text-[#0c1e41] transition-colors">Blog</Link>
            </div>
            <div className="flex items-center gap-3">
              <Link to="/login">
                <Button variant="ghost" size="sm" className="text-slate-700 hover:text-[#0c1e41] hidden sm:flex">
                  Sign In
                </Button>
              </Link>
              <Link to="/register">
                <Button size="sm" className="bg-[#0c1e41] hover:bg-[#091629] text-white shadow-sm">
                  Get Started Free
                </Button>
              </Link>
              <button
                className="md:hidden p-2 text-slate-600"
                onClick={() => setMobileOpen(!mobileOpen)}
              >
                {mobileOpen ? <X size={20} /> : <Menu size={20} />}
              </button>
            </div>
          </div>
        </div>
        {/* Mobile menu */}
        {mobileOpen && (
          <div className="md:hidden bg-white border-t border-slate-200 py-4 px-6 space-y-3">
            {["Features", "How It Works", "Pricing", "API", "FAQ"].map((item) => (
              <a
                key={item}
                href={`#${item.toLowerCase().replace(" ", "-")}`}
                onClick={() => setMobileOpen(false)}
                className="block text-sm font-medium text-slate-700 hover:text-[#0c1e41] py-1"
              >
                {item}
              </a>
            ))}
            <div className="flex gap-3 pt-2">
              <Link to="/login" className="flex-1">
                <Button variant="outline" size="sm" className="w-full border-slate-300 text-slate-700">Sign In</Button>
              </Link>
              <Link to="/register" className="flex-1">
                <Button size="sm" className="w-full bg-[#0c1e41] text-white">Start Free</Button>
              </Link>
            </div>
          </div>
        )}
      </nav>

      {/* ── HERO ── */}
      <section className="relative overflow-hidden bg-gradient-to-br from-slate-50 via-white to-[#eef1f8] py-20 lg:py-28">
        {/* Background pattern */}
        <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjAiIGhlaWdodD0iNjAiIHZpZXdCb3g9IjAgMCA2MCA2MCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48ZyBmaWxsPSJub25lIiBmaWxsLXJ1bGU9ImV2ZW5vZGQiPjxnIGZpbGw9IiMyNTYzZWIiIGZpbGwtb3BhY2l0eT0iMC4wMyI+PHBhdGggZD0iTTM2IDM0djZoNnYtNmgtNnptNiA2djZoNnYtNmgtNnptLTEyIDB2Nmg2di02aC02em0xMiAtMTJ2Nmg2di02aC02eiIvPjwvZz48L2c+PC9zdmc+')] opacity-60" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto text-center">
            <Badge className="mb-6 bg-[#eef1f8] text-[#0c1e41] border border-[#c0cfe8] hover:bg-[#eef1f8] px-4 py-1.5 text-sm font-medium">
              Nigeria's #1 Bulk SMS Platform
            </Badge>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold text-slate-900 tracking-tight mb-6 leading-tight">
              Reach Every Nigerian
              <br />
              <span className="gradient-text">Instantly & Reliably</span>
            </h1>
            <p className="text-lg sm:text-xl text-slate-600 mb-10 max-w-2xl mx-auto leading-relaxed">
              Send millions of SMS across MTN, Airtel, Glo and 9mobile with 99%+ delivery rates.
              Enterprise-grade infrastructure. Pay-as-you-go pricing.
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <Link to="/register">
                <Button size="lg" className="bg-[#0c1e41] hover:bg-[#091629] text-white px-8 h-12 text-base font-semibold shadow-lg shadow-blue-600/25 w-full sm:w-auto">
                  Start Sending Free <ArrowRight size={18} className="ml-2" />
                </Button>
              </Link>
              <a href="#how-it-works">
                <Button size="lg" variant="outline" className="border-slate-300 text-slate-700 hover:bg-slate-50 px-8 h-12 text-base w-full sm:w-auto">
                  See How It Works
                </Button>
              </a>
            </div>
            <p className="mt-4 text-xs text-slate-500">No credit card required · Setup in 60 seconds · Cancel anytime</p>
          </div>
        </div>
      </section>

      {/* ── STATS BAR ── */}
      <section className="bg-[#0c1e41] py-10">
        <div className="max-w-5xl mx-auto px-4 sm:px-6">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-8 text-center text-white">
            {[
              { value: "50M+", label: "SMS Sent Monthly" },
              { value: "5,000+", label: "Active Businesses" },
              { value: "99.2%", label: "Delivery Rate" },
              { value: "24/7", label: "Customer Support" },
            ].map((s) => (
              <div key={s.label}>
                <div className="text-3xl sm:text-4xl font-extrabold mb-1">{s.value}</div>
                <div className="text-[#adc0e0] text-sm font-medium">{s.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── FEATURES ── */}
      <section id="features" className="py-20 lg:py-28 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-14">
            <Badge className="mb-4 bg-[#eef1f8] text-[#0c1e41] border-[#c0cfe8]">Platform Features</Badge>
            <h2 className="text-3xl sm:text-4xl font-extrabold text-slate-900 mb-4">
              Everything You Need to Communicate at Scale
            </h2>
            <p className="text-slate-600 max-w-2xl mx-auto text-lg">
              Built for Nigerian businesses — from startups to enterprises — with the tools and reliability you need.
            </p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {FEATURES.map((f) => (
              <div key={f.title} className="feature-card">
                <div className={`w-11 h-11 rounded-xl flex items-center justify-center mb-4 ${f.color}`}>
                  <f.icon size={20} />
                </div>
                <h3 className="text-base font-bold text-slate-900 mb-2">{f.title}</h3>
                <p className="text-sm text-slate-600 leading-relaxed">{f.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── HOW IT WORKS ── */}
      <section id="how-it-works" className="py-20 lg:py-28 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-14">
            <Badge className="mb-4 bg-[#eef1f8] text-[#0c1e41] border-[#c0cfe8]">Simple Process</Badge>
            <h2 className="text-3xl sm:text-4xl font-extrabold text-slate-900 mb-4">
              Go Live in 3 Simple Steps
            </h2>
            <p className="text-slate-600 max-w-2xl mx-auto">
              From signup to sending your first SMS — it takes less than 5 minutes.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 lg:gap-12 relative">
            {/* Connector lines */}
            <div className="hidden md:block absolute top-14 left-1/3 right-1/3 h-0.5 bg-[#c0cfe8]" />
            {STEPS.map((step, i) => (
              <div key={step.step} className="step-card relative">
                <div className="w-16 h-16 rounded-2xl bg-[#0c1e41] text-white flex items-center justify-center text-xl font-extrabold mb-5 shadow-lg shadow-blue-600/25 mx-auto">
                  {step.step}
                </div>
                <h3 className="text-lg font-bold text-slate-900 mb-3">{step.title}</h3>
                <p className="text-slate-600 text-sm leading-relaxed">{step.desc}</p>
              </div>
            ))}
          </div>
          <div className="text-center mt-12">
            <Link to="/register">
              <Button className="bg-[#0c1e41] hover:bg-[#091629] text-white px-8 h-11 shadow-lg shadow-blue-600/20">
                Get Started Now <ArrowRight size={16} className="ml-2" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* ── USE CASES ── */}
      <section className="py-20 lg:py-28 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-14">
            <Badge className="mb-4 bg-[#eef1f8] text-[#0c1e41] border-[#c0cfe8]">Industry Solutions</Badge>
            <h2 className="text-3xl sm:text-4xl font-extrabold text-slate-900 mb-4">
              Trusted Across Every Sector
            </h2>
            <p className="text-slate-600 max-w-xl mx-auto">
              From fintech to healthcare, Sendora powers communication for Nigeria's fastest-growing industries.
            </p>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4">
            {USE_CASES.map((uc) => (
              <div key={uc.label} className="bg-slate-50 hover:bg-[#eef1f8] border border-slate-200 hover:border-[#c0cfe8] rounded-2xl p-4 text-center transition-all duration-200 cursor-default group">
                <div className="w-10 h-10 rounded-xl bg-white border border-slate-200 group-hover:border-[#c0cfe8] flex items-center justify-center mx-auto mb-3 shadow-sm">
                  <uc.icon size={18} className="text-[#0c1e41]" />
                </div>
                <div className="text-xs font-semibold text-slate-900 mb-1">{uc.label}</div>
                <div className="text-xs text-slate-500 leading-tight">{uc.desc}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── TESTIMONIALS ── */}
      <section className="py-20 lg:py-28 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-14">
            <Badge className="mb-4 bg-[#eef1f8] text-[#0c1e41] border-[#c0cfe8]">Customer Stories</Badge>
            <h2 className="text-3xl sm:text-4xl font-extrabold text-slate-900 mb-4">
              Loved by Nigerian Businesses
            </h2>
            <p className="text-slate-600 max-w-xl mx-auto">
              Join thousands of businesses already growing with Sendora.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {TESTIMONIALS.map((t) => (
              <div key={t.name} className="bg-white border border-slate-200 rounded-2xl p-7 shadow-sm hover:shadow-md transition-all duration-200">
                <div className="flex gap-1 mb-4">
                  {Array(t.rating).fill(0).map((_, i) => (
                    <Star key={i} size={14} className="text-amber-400 fill-amber-400" />
                  ))}
                </div>
                <p className="text-slate-700 text-sm leading-relaxed mb-6 italic">
                  "{t.quote}"
                </p>
                <div className="flex items-center gap-3">
                  <div className={`w-10 h-10 rounded-full ${t.color} flex items-center justify-center text-white text-sm font-bold shrink-0`}>
                    {t.initials}
                  </div>
                  <div>
                    <div className="text-sm font-semibold text-slate-900">{t.name}</div>
                    <div className="text-xs text-slate-500">{t.role}, {t.company}</div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── PRICING ── */}
      <section id="pricing" className="py-20 lg:py-28 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-14">
            <Badge className="mb-4 bg-[#eef1f8] text-[#0c1e41] border-[#c0cfe8]">Transparent Pricing</Badge>
            <h2 className="text-3xl sm:text-4xl font-extrabold text-slate-900 mb-4">
              Simple, Pay-As-You-Go Pricing
            </h2>
            <p className="text-slate-600 max-w-2xl mx-auto">
              No subscriptions. No hidden fees. Buy credits when you need them. The more you buy, the less you pay per SMS.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 lg:gap-8 max-w-5xl mx-auto items-center">
            {PRICING.map((plan) => (
              <div
                key={plan.name}
                className={plan.featured ? "pricing-card-featured" : "pricing-card"}
              >
                {plan.featured && (
                  <div className="flex justify-center mb-4">
                    <Badge className="bg-white/20 text-white border-white/30 text-xs">Most Popular</Badge>
                  </div>
                )}
                <div className={`text-sm font-semibold mb-2 ${plan.featured ? "text-[#adc0e0]" : "text-slate-500"}`}>
                  {plan.name}
                </div>
                <div className={`text-4xl font-extrabold mb-1 ${plan.featured ? "text-white" : "text-slate-900"}`}>
                  {plan.price}
                </div>
                <div className={`text-sm mb-1 ${plan.featured ? "text-[#adc0e0]" : "text-slate-500"}`}>
                  {plan.credits.toLocaleString()} credits ({plan.perSMS}/SMS)
                </div>
                {plan.bonus && (
                  <div className={`text-xs font-semibold mb-5 ${plan.featured ? "text-[#adc0e0]" : "text-emerald-600"}`}>
                    {plan.bonus}
                  </div>
                )}
                {!plan.bonus && <div className="mb-5" />}
                <ul className="space-y-2.5 flex-1 mb-6">
                  {plan.features.map((f) => (
                    <li key={f} className="flex items-center gap-2.5 text-sm">
                      <div className={`w-4 h-4 rounded-full flex items-center justify-center shrink-0 ${plan.featured ? "bg-white/20" : "bg-[#eef1f8]"}`}>
                        <Check size={10} className={plan.featured ? "text-white" : "text-[#0c1e41]"} />
                      </div>
                      <span className={plan.featured ? "text-[#dce4f0]" : "text-slate-700"}>{f}</span>
                    </li>
                  ))}
                </ul>
                <Link to="/register">
                  <Button
                    className={`w-full ${
                      plan.featured
                        ? "bg-white text-[#0c1e41] hover:bg-[#eef1f8]"
                        : "bg-[#0c1e41] hover:bg-[#091629] text-white"
                    }`}
                  >
                    Get Started
                  </Button>
                </Link>
              </div>
            ))}
          </div>
          <div className="text-center mt-10">
            <div className="inline-flex items-center gap-2 bg-slate-50 border border-slate-200 rounded-full px-5 py-2 text-sm text-slate-600">
              <Building size={14} className="text-[#0c1e41]" />
              Need high-volume pricing?{" "}
              <a href="mailto:hello@sendora.com" className="text-[#0c1e41] font-semibold hover:underline">
                Contact enterprise sales
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* ── API / DEVELOPER ── */}
      <section id="api" className="py-20 lg:py-28 bg-slate-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-center">
            <div>
              <Badge className="mb-5 bg-[#eef1f8]0/20 text-blue-400 border-blue-500/30">Developer First</Badge>
              <h2 className="text-3xl sm:text-4xl font-extrabold text-white mb-5">
                Powerful API.
                <br />
                <span className="text-blue-400">Simple Integration.</span>
              </h2>
              <p className="text-slate-400 text-lg mb-7 leading-relaxed">
                Our RESTful API makes it easy to integrate SMS into any application.
                Full documentation, code examples, and sandbox environment included.
              </p>
              <ul className="space-y-3 mb-8">
                {[
                  "Bearer token authentication",
                  "Webhooks for delivery reports",
                  "Idempotent requests",
                  "Rate limiting & retry logic",
                  "SDKs: Node.js, Python, PHP",
                ].map((f) => (
                  <li key={f} className="flex items-center gap-3 text-slate-300 text-sm">
                    <div className="w-5 h-5 rounded-full bg-[#eef1f8]0/20 flex items-center justify-center shrink-0">
                      <Check size={11} className="text-blue-400" />
                    </div>
                    {f}
                  </li>
                ))}
              </ul>
              <Link to="/register">
                <Button className="bg-[#0c1e41] hover:bg-[#eef1f8]0 text-white px-6 h-11">
                  Get Your API Key <Code size={15} className="ml-2" />
                </Button>
              </Link>
            </div>
            <div>
              <div className="bg-slate-800 rounded-2xl overflow-hidden border border-slate-700 shadow-2xl">
                <div className="flex items-center gap-2 px-4 py-3 border-b border-slate-700 bg-slate-800/80">
                  {["bg-red-500", "bg-amber-500", "bg-emerald-500"].map((c) => (
                    <div key={c} className={`w-3 h-3 rounded-full ${c}`} />
                  ))}
                  <span className="text-xs text-slate-500 ml-2 font-mono">POST /api/sms/send</span>
                </div>
                <pre className="p-5 text-xs font-mono text-slate-300 leading-relaxed overflow-x-auto">
{`curl -X POST \\
  https://api.sendora.com/api/sms/send \\
  -H "Authorization: Bearer sk_live_•••••" \\
  -H "Content-Type: application/json" \\
  -d '{
    "sender_id": "MYBRAND",
    "recipients": [
      "2348012345678",
      "2347098765432"
    ],
    "message": "Your OTP is 4821. Expires in 10 minutes.",
    "callback_url": "https://myapp.com/sms/webhook"
  }'`}
                </pre>
                <div className="px-5 pb-4">
                  <div className="bg-slate-900/60 rounded-lg p-3 text-xs font-mono">
                    <div className="text-emerald-400">{"{"}</div>
                    <div className="pl-4 text-slate-300">  "status": <span className="text-emerald-400">"success"</span>,</div>
                    <div className="pl-4 text-slate-300">  "sent": <span className="text-blue-400">2</span>,</div>
                    <div className="pl-4 text-slate-300">  "credits_used": <span className="text-blue-400">2</span>,</div>
                    <div className="pl-4 text-slate-300">  "message_ids": [<span className="text-amber-400">"MSG_AB12..."</span>]</div>
                    <div className="text-emerald-400">{"}"}</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── FAQ ── */}
      <section id="faq" className="py-20 lg:py-28 bg-white">
        <div className="max-w-3xl mx-auto px-4 sm:px-6">
          <div className="text-center mb-12">
            <Badge className="mb-4 bg-[#eef1f8] text-[#0c1e41] border-[#c0cfe8]">FAQ</Badge>
            <h2 className="text-3xl sm:text-4xl font-extrabold text-slate-900 mb-4">
              Frequently Asked Questions
            </h2>
            <p className="text-slate-600">
              Can't find what you're looking for? Email us at{" "}
              <a href="mailto:hello@sendora.com" className="text-[#0c1e41] hover:underline font-medium">
                hello@sendora.com
              </a>
            </p>
          </div>
          <div className="bg-white border border-slate-200 rounded-2xl px-6 shadow-sm">
            {FAQS.map((faq) => (
              <FAQItem key={faq.q} {...faq} />
            ))}
          </div>
        </div>
      </section>

      {/* ── FINAL CTA ── */}
      <section className="py-20 bg-gradient-to-br from-[#0c1e41] to-[#060e1c]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 text-center">
          <h2 className="text-3xl sm:text-4xl font-extrabold text-white mb-5">
            Ready to Reach Every Nigerian?
          </h2>
          <p className="text-[#adc0e0] text-lg mb-8 max-w-xl mx-auto">
            Join 5,000+ Nigerian businesses using Sendora. Start free, no credit card needed.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <Link to="/register">
              <Button size="lg" className="bg-white text-[#0c1e41] hover:bg-[#eef1f8] px-8 h-12 text-base font-semibold shadow-xl w-full sm:w-auto">
                Create Free Account <ArrowRight size={18} className="ml-2" />
              </Button>
            </Link>
            <Link to="/login">
              <Button size="lg" variant="ghost" className="text-[#dce4f0] hover:text-white hover:bg-[#091629]/50 px-8 h-12 text-base w-full sm:w-auto">
                Sign Into Dashboard
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* ── FOOTER ── */}
      <footer className="bg-slate-900 text-slate-400 py-14">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 mb-10">
            <div className="col-span-2 md:col-span-1">
              <div className="mb-4">
                <img src="/sendora-logo.webp" alt="Sendora" className="h-8 w-auto brightness-0 invert" />
              </div>
              <p className="text-sm leading-relaxed text-slate-500 mb-5">
                Nigeria's premier bulk SMS platform. Reliable delivery. Transparent pricing.
              </p>
              {/* Social links */}
              <div className="flex items-center gap-3">
                <a href="https://twitter.com/SendoraNg" target="_blank" rel="noopener noreferrer" aria-label="Twitter" className="w-8 h-8 rounded-full bg-slate-800 hover:bg-[#0c1e41] flex items-center justify-center transition-colors">
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor" className="text-slate-400 hover:text-white"><path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-4.714-6.231-5.401 6.231H2.74l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" /></svg>
                </a>
                <a href="https://linkedin.com/company/sendora-ng" target="_blank" rel="noopener noreferrer" aria-label="LinkedIn" className="w-8 h-8 rounded-full bg-slate-800 hover:bg-[#0c1e41] flex items-center justify-center transition-colors">
                  <svg width="13" height="13" viewBox="0 0 24 24" fill="currentColor" className="text-slate-400 hover:text-white"><path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433a2.062 2.062 0 01-2.063-2.065 2.064 2.064 0 112.063 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z" /></svg>
                </a>
                <a href="https://facebook.com/SendoraNg" target="_blank" rel="noopener noreferrer" aria-label="Facebook" className="w-8 h-8 rounded-full bg-slate-800 hover:bg-[#0c1e41] flex items-center justify-center transition-colors">
                  <svg width="13" height="13" viewBox="0 0 24 24" fill="currentColor" className="text-slate-400 hover:text-white"><path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z" /></svg>
                </a>
                <a href="https://instagram.com/SendoraNg" target="_blank" rel="noopener noreferrer" aria-label="Instagram" className="w-8 h-8 rounded-full bg-slate-800 hover:bg-[#0c1e41] flex items-center justify-center transition-colors">
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor" className="text-slate-400 hover:text-white"><path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zM12 0C8.741 0 8.333.014 7.053.072 2.695.272.273 2.69.073 7.052.014 8.333 0 8.741 0 12c0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98C8.333 23.986 8.741 24 12 24c3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98C15.668.014 15.259 0 12 0zm0 5.838a6.162 6.162 0 100 12.324 6.162 6.162 0 000-12.324zM12 16a4 4 0 110-8 4 4 0 010 8zm6.406-11.845a1.44 1.44 0 100 2.881 1.44 1.44 0 000-2.881z" /></svg>
                </a>
              </div>
            </div>
            <div>
              <h4 className="text-white text-sm font-semibold mb-4">Platform</h4>
              <ul className="space-y-2 text-sm">
                <li><Link to="/coverage" className="hover:text-white transition-colors">Coverage</Link></li>
                <li><a href="#features" className="hover:text-white transition-colors">Features</a></li>
                <li><a href="#pricing" className="hover:text-white transition-colors">Pricing</a></li>
                <li><Link to="/blog" className="hover:text-white transition-colors">Blog</Link></li>
                <li><Link to="/login" className="hover:text-white transition-colors">Sign In</Link></li>
              </ul>
            </div>
            <div>
              <h4 className="text-white text-sm font-semibold mb-4">Company</h4>
              <ul className="space-y-2 text-sm">
                <li><Link to="/about" className="hover:text-white transition-colors">About Us</Link></li>
                <li><Link to="/contact" className="hover:text-white transition-colors">Contact</Link></li>
              </ul>
            </div>
            <div>
              <h4 className="text-white text-sm font-semibold mb-4">Legal</h4>
              <ul className="space-y-2 text-sm">
                <li><Link to="/privacy" className="hover:text-white transition-colors">Privacy Policy</Link></li>
                <li><Link to="/terms" className="hover:text-white transition-colors">Terms & Conditions</Link></li>
                <li><Link to="/coverage" className="hover:text-white transition-colors">NCC Coverage</Link></li>
              </ul>
            </div>
          </div>
          <div className="border-t border-slate-800 pt-6 flex flex-col sm:flex-row items-center justify-between gap-3 text-sm">
            <p>© {new Date().getFullYear()} Sendora. All rights reserved.</p>
            <p className="text-slate-600">Built for Nigerian businesses.</p>
          </div>
        </div>
      </footer>
      <WhatsAppButton />
    </div>
  );
}
